import React, { memo } from 'react'
import { Controller } from 'react-hook-form'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import ControlledCurrencyInput from 'transfix-form/components/ControlledCurrencyInput'
import ControlledDatePicker from 'transfix-form/components/ControlledDatePicker'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import ControlledTextArea from 'transfix-form/components/ControlledTextArea'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import Button from 'transfix-ui/components/Button'
import { Select, ISelectProps } from 'transfix-ui/components/Select'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { RATE_TYPE_OPTIONS } from 'apps/fleet-planner/constants/dedicated-lanes'
import { useUserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { IUserMotorCarrier } from 'apps/fleet-planner/types'
import { useCreateDedicatedLanesBid } from 'apps/fleet-planner/types/apiTypes'
import { getCarrierOptions } from 'apps/fleet-planner/utils/getCarrierOptions'
import formatCurrency from 'utils/formatCurrency'
import { useDedicatedLanesBidForm } from '../../hooks/useDedicatedLanesBidForm'
import { TDedicatedLanesBidFormSchema } from '../../schema/DedicatedLanesBidFormSchema'

const useDedicatedLanesBidFormBodyStyles = makeStyles(theme => ({
  avgContainer: {
    background: brandColors.coolGray1,
    borderRadius: 4,
    padding: '12px 16px',
    textAlign: 'center',
  },
  rateAmount: {
    '& .label.required': {
      display: 'none',
    },
    '& .text-input-container': {
      border: 'none',
      marginTop: 20,
      marginBottom: 20,
    },
    '& .validation-message': {
      marginBottom: 5,
      marginTop: 0,
      textAlign: 'center',
    },
    '& .text-input-container input': {
      fontSize: 34,
      textAlign: 'center',
    },
  },
  formField: {
    marginTop: theme.spacing(2),
  },
}))

export interface IDedicatedLanesBidFormBodyProps {
  userMotorCarriers: IUserMotorCarrier[]
  dedicatedLaneId: number
  averageAmount: number | undefined
  onSuccess: () => void
}

const DedicatedLanesBidFormBody = memo<IDedicatedLanesBidFormBodyProps>(
  ({ averageAmount, dedicatedLaneId, onSuccess, userMotorCarriers }) => {
    const classes = useDedicatedLanesBidFormBodyStyles()
    const { openToast } = useGlobalToast()
    const { trackEvent } = useFMSAnalytics()

    // Motor Carriers
    const carriersOptions = getCarrierOptions(userMotorCarriers)
    const [firstUserMotorCarrier] = userMotorCarriers || []
    const { motorCarrier } = firstUserMotorCarrier || {}
    const { dotNumber, mcNumber } = motorCarrier || {}

    // User Data
    const { userData } = useUserContext()
    const { user } = userData || {}
    const { email, phone = '' } = user || {}

    // Mutation
    const { loading: isLoadingBid, mutate: createBid } = useCreateDedicatedLanesBid({})

    const defaultFormValues = {
      dotNumber,
      mcNumber,
      contactEmail: email,
      contactPhone: phone,
    }

    const { control, errors: formErrors, handleSubmit, reset, setValue } = useDedicatedLanesBidForm(
      {
        defaultValues: defaultFormValues,
      }
    )

    const resetDedicatedLanesForm = () => {
      reset({
        ...defaultFormValues,
        weeklyLoadVolume: '',
        truckCount: '',
        driverCount: '',
        trailerCount: '',
      })
    }

    const submitHandler = async (data: TDedicatedLanesBidFormSchema) => {
      const { weeklyLoadVolume } = data || {}
      try {
        await createBid({
          dedicatedLanesBid: {
            shipperRateId: dedicatedLaneId,
            ...data,
            weeklyLoadVolume: Number(weeklyLoadVolume),
          },
        })
        openToast({
          toastType: 'default',
          toastMessage: 'Thanks for your bid. We will contact you as soon as possible.',
        })
        onSuccess()
        resetDedicatedLanesForm()
        trackEvent('Dedicated Lanes', 'SUBMITTED_DEDICATED_LANE_BID')
      } catch (e) {
        openToast({
          toastType: 'alert',
          toastMessage: e.message || 'Something went wrong, please contact us.',
        })
      }
    }

    const submitFormHandler = handleSubmit(submitHandler)

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h3'>Submit Rate</Typography>
        </Grid>
        <Grid item xs={12} md={12}>
          <ControlledCurrencyInput
            name='rateAmount'
            control={control}
            CurrencyInputProps={{
              large: true,
              required: true,
              autoFocus: true,
              hideRequiredLabel: true,
              dataTest: 'dedicated-lanes-bid-form-rate-amount',
              className: classes.rateAmount,
              validationMessage: formErrors?.rateAmount?.message || '',
              invalid: !!formErrors?.rateAmount?.message,
              placeholder: '$0',
            }}
          />
        </Grid>
        {averageAmount && (
          <Grid item xs={12}>
            <div className={classes.avgContainer}>
              Average rate for this lane is{' '}
              <strong>{formatCurrency(averageAmount, undefined, true)}</strong>
            </div>
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller
            name='dotNumber'
            control={control}
            render={({ onChange, value }) => {
              const changeHandler: NonNullable<ISelectProps['onChange']> = event => {
                onChange(event.target.value)
                const selectedCarrier = userMotorCarriers.find(
                  m => m?.motorCarrier?.dotNumber === event.target.value
                )
                setValue('mcNumber', selectedCarrier?.motorCarrier?.mcNumber!)
              }

              return (
                <Select
                  large
                  required
                  dataTest='dedicated-lanes-bid-form-carrier-select'
                  disabled={userMotorCarriers?.length === 1 && value}
                  placeholder='Select a carrier'
                  value={value}
                  label='Carrier'
                  options={carriersOptions}
                  onChange={changeHandler}
                  error={!!formErrors?.dotNumber}
                  validationMessage={formErrors?.dotNumber?.message}
                />
              )
            }}
          />
          <ControlledHiddenInput name='mcNumber' control={control} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledTextInput
            name='weeklyLoadVolume'
            control={control}
            TextInputProps={{
              large: true,
              required: true,
              dataTest: 'dedicated-lanes-bid-form-weekly-load-volume',
              type: 'number',
              label: 'Weekly Load Volume',
              error: !!formErrors?.weeklyLoadVolume,
              validationMessage: formErrors?.weeklyLoadVolume?.message,
              placeholder: '# of loads',
              InputProps: { inputProps: { inputMode: 'numeric' } },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledSelect
            name='rateType'
            control={control}
            SelectProps={{
              placeholder: 'Select an option',
              large: true,
              required: true,
              dataTest: 'dedicated-lanes-bid-form-rate-type-select',
              label: 'Rate Type',
              options: RATE_TYPE_OPTIONS,
              error: !!formErrors?.rateType,
              validationMessage: formErrors?.rateType?.message,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCheckbox
            name='canDoWeekends'
            control={control}
            CheckboxProps={{
              label: 'I can take loads on the weekends',
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextInput
            name='truckCount'
            control={control}
            TextInputProps={{
              large: true,
              required: true,
              dataTest: 'dedicated-lanes-bid-form-truck-count',
              label: 'Trucks',
              error: !!formErrors?.truckCount,
              validationMessage: formErrors?.truckCount?.message,
              placeholder: '0',
              className: 'fs-mask',
              type: 'number',
              InputProps: { inputProps: { inputMode: 'numeric' } },
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextInput
            name='driverCount'
            control={control}
            TextInputProps={{
              large: true,
              required: true,
              dataTest: 'dedicated-lanes-bid-form-driver-count',
              label: 'Drivers',
              error: !!formErrors?.driverCount,
              validationMessage: formErrors?.driverCount?.message,
              placeholder: '0',
              className: 'fs-mask',
              type: 'number',
              InputProps: { inputProps: { inputMode: 'numeric' } },
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextInput
            name='trailerCount'
            control={control}
            TextInputProps={{
              large: true,
              required: true,
              dataTest: 'dedicated-lanes-bid-form-trailer-count',
              label: 'Trailers',
              error: !!formErrors?.trailerCount,
              validationMessage: formErrors?.trailerCount?.message,
              placeholder: '0',
              className: 'fs-mask',
              type: 'number',
              InputProps: { inputProps: { inputMode: 'numeric' } },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledDatePicker
            name='estimatedStartDate'
            control={control}
            defaultValue={null}
            DatePickerProps={{
              dataTest: 'dedicated-lanes-bid-form-estimated-start-date',
              placeholder: 'Select a Start Date',
              label: 'Estimated Start Date',
              required: true,
              minDate: Date.now(),
              error: !!formErrors?.estimatedStartDate,
              validationMessage: formErrors?.estimatedStartDate?.message,
            }}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <ControlledTextInput
            name='contactEmail'
            control={control}
            TextInputProps={{
              large: true,
              required: true,
              label: 'Contact Email',
              dataTest: 'dedicated-lanes-bid-form-contact-email',
              error: !!formErrors?.contactEmail,
              validationMessage: formErrors?.contactEmail?.message,
              placeholder: 'Contact Email',
              className: 'fs-mask',
            }}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <ControlledPhoneInput
            control={control}
            name='contactPhone'
            PhoneInputProps={{
              dataTest: 'dedicated-lanes-bid-form-contact-phone',
              className: 'mt1 fs-mask',
              placeholder: '(000) 000-0000',
              required: true,
              large: true,
              label: 'Contact Phone Number',
              invalid: !!formErrors.contactPhone,
              validationMessage: formErrors?.contactPhone?.message,
              inputMode: 'numeric',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledTextArea
            name='contactMessage'
            control={control}
            TextAreaProps={{
              dataTest: 'dedicated-lanes-bid-form-contact-message',
              label: 'Message (optional)',
              placeholder: 'Enter a message or notes about your rate',
              size: 'small',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            data-test='dedicated-lanes-bid-form-submit-button'
            color='primary'
            disabled={isLoadingBid}
            fullWidth
            size='large'
            onClick={submitFormHandler}>
            Submit Rate
          </Button>
        </Grid>
      </Grid>
    )
  }
)

export default DedicatedLanesBidFormBody
